import { createContext, useContext } from 'react';
import { FeedbackType, FeedbackFormData } from '@/types/feedback';
import type { FabMode } from '@/features/chat/types';

// Extended context interface that includes both feedback and chat
export interface FeedbackChatContextType {
  // Feedback functionality
  isFeedbackOpen: boolean;
  openFeedback: (type?: FeedbackType) => void;
  closeFeedback: () => void;
  submitFeedback: (data: FeedbackFormData) => Promise<void>;
  isSubmitting: boolean;
  currentPageSource: string;
  feedbackType?: FeedbackType;
  
  // Chat functionality
  isChatOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  
  // FAB mode and badge
  fabMode: FabMode;
  setFabMode: (mode: FabMode) => void;
  unreadChatCount: number;
}

export const FeedbackChatContext = createContext<FeedbackChatContextType | undefined>(undefined);

export const useFeedbackChat = () => {
  const context = useContext(FeedbackChatContext);
  if (context === undefined) {
    throw new Error('useFeedbackChat must be used within a FeedbackChatProvider');
  }
  return context;
};

// Backward compatibility - export the original feedback interface
export const useFeedback = () => {
  const context = useFeedbackChat();
  return {
    isOpen: context.isFeedbackOpen,
    openFeedback: context.openFeedback,
    closeFeedback: context.closeFeedback,
    submitFeedback: context.submitFeedback,
    isSubmitting: context.isSubmitting,
    currentPageSource: context.currentPageSource,
    feedbackType: context.feedbackType,
  };
};
